import { forwardRef } from 'react';
import { Button as TamaguiButton, type TamaguiElement, type SizeTokens } from 'tamagui';
import XStack from '../../XStack/XStack';
import YStack from '../../YStack/YStack';
import Icon from '../../Icon/Icon';
import Text from '../../Text/Text';
import Spinner from '../../Spinner/Spinner';
import type { Props as SelectProps } from '../Select';

const getHeight = (size: SelectProps['size']): SizeTokens => {
  switch (size) {
    case 'sm':
      return '$8';
    case 'md':
      return '$10';
    default:
      return '$10';
  }
};

const getBorderRadius = (variant: SelectProps['variant']): SizeTokens => {
  switch (variant) {
    case 'default':
      return '$0';
    case 'rounded':
      return 9999;
    default:
      return '$0';
  }
};

const getFontSize = (size: SelectProps['size']): SizeTokens => {
  switch (size) {
    case 'sm':
      return '$12';
    case 'md':
      return '$14';
    default:
      return '$14';
  }
};

type Props = {
  children: React.ReactNode;
  size: SelectProps['size'];
  variant: SelectProps['variant'];
  isOpen: boolean;
  loading?: SelectProps['loading'];
  centered?: SelectProps['centered'];
  error?: boolean;
};

const ListboxButton = forwardRef<TamaguiElement, Props>(
  ({ children, size, variant, isOpen, loading, centered, error, ...restProps }, ref) => {
    return (
      <TamaguiButton
        ref={ref}
        borderWidth={1}
        borderColor={error ? '$error' : '$border'}
        minHeight={getHeight(size)}
        px="$4"
        borderRadius={getBorderRadius(variant)}
        hoverStyle={{
          borderWidth: 1,
          borderColor: '$border',
        }}
        focusStyle={{
          borderWidth: 1,
          borderColor: '$basePale',
        }}
        testID="select-listbox-button"
        {...restProps}
      >
        <XStack flex={1} ai="center" justifyContent={centered ? 'center' : 'space-between'}>
          <Text fontSize={getFontSize(size)} numberOfLines={1}>
            {children}
          </Text>

          <YStack right={0} ml="$2">
            {loading ? (
              <Spinner size="small" color="$contrastPale" />
            ) : (
              <Icon icon={isOpen ? 'chevronUp' : 'chevronDown'} size={8 * 2} color="$darkGray" />
            )}
          </YStack>
        </XStack>
      </TamaguiButton>
    );
  },
);

export default ListboxButton;
